import React, { useState } from "react";
import {
  AlertTriangle,
  Camera,
  Car,
  Check,
  ClipboardCheck,
  Fuel,
  ListChecks,
  Plus,
  Printer,
  Save,
  Search,
  X,
} from "lucide-react";
import { Assinatura, Fotos, MapaAvarias } from "../ui/captura";
import { Btn, CabecalhoFolha, Field, Modal, Pill, Placa, Vazio, usarServidor } from "../ui/componentes";
import { GRUPOS_CHECK, fdata, num } from "../ui/formato";

/* ══════════════════════════════════════════════════════════════════
   RECEPÇÃO — CHECKLIST DE ENTRADA
   ══════════════════════════════════════════════════════════════════ */
/**
 * A folha do checklist, usada nos dois lugares.
 *
 * Ela vivia solta dentro do modo lista. O botão "Imprimir / salvar PDF" do
 * modo de preenchimento chamava window.print() com a folha fora do DOM — e
 * como a impressão esconde tudo e revela só o .folha, saía uma página em
 * branco. O mecânico imprimia para o cliente assinar e recebia papel vazio.
 *
 * Serve para o checklist salvo e para o que está sendo preenchido: depois do
 * mapeamento os dois têm os mesmos campos.
 */
function FolhaChecklist({ ck, db, vei, cliDoVei }) {
  if (!ck) return null;
  return (
        <div className="folha">
          <CabecalhoFolha of={db.oficina} titulo="Checklist de entrada" numero={ck.id}
            data={fdata(ck.data)} />

          <h2>Cliente e veículo</h2>
          <div className="par">
            <div><b>Cliente:</b> {cliDoVei(ck.vei)?.nome ?? "—"}</div>
            <div><b>Telefone:</b> {cliDoVei(ck.vei)?.tel || "—"}</div>
            <div><b>Veículo:</b> {[vei(ck.vei)?.marca, vei(ck.vei)?.modelo].filter(Boolean).join(" ") || "—"}</div>
            <div><b>Placa:</b> {vei(ck.vei)?.placa ?? "—"}</div>
            <div><b>Hodômetro:</b> {num(ck.km)} km</div>
            <div><b>Combustível:</b> {["Reserva", "1/4", "1/2", "3/4", "Cheio"][ck.comb] ?? "—"}</div>
          </div>

          {/* A inspeção não saía na folha. Um checklist impresso sem os
              itens não diz o estado de peça nenhuma, que é justamente o que
              ele existe para provar depois. */}
          <h2>Inspeção visual</h2>
          {Object.keys(ck.itens ?? {}).length ? (
            <table>
              <thead><tr><th style={{ width: "34%" }}>Grupo</th><th>Item</th><th style={{ width: "18%" }}>Estado</th></tr></thead>
              <tbody>
                {Object.entries(GRUPOS_CHECK).flatMap(([grupo, itens]) =>
                  itens.filter((it) => ck.itens?.[it]).map((it) => (
                    <tr key={grupo + it}>
                      <td>{grupo}</td><td>{it}</td>
                      <td>{({ ok: "Bom", warn: "Atenção", crit: "Crítico" })[ck.itens[it]] ?? ck.itens[it]}</td>
                    </tr>
                  )))}
              </tbody>
            </table>
          ) : (
            <div className="obs">Nenhum item inspecionado.</div>
          )}

          <h2>Avarias registradas na entrada</h2>
          {ck.avarias?.length ? (
            <table>
              <thead><tr><th style={{ width: "34%" }}>Onde</th><th>O que</th></tr></thead>
              <tbody>
                {ck.avarias.map((a, i) => (
                  <tr key={i}><td>{a.peca ?? a.local ?? "—"}</td><td>{a.tipo ?? a.desc ?? "—"}</td></tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="obs">Nenhuma avaria registrada na entrada do veículo.</div>
          )}

          {ck.pertences && <><h2>Pertences no veículo</h2><div className="obs">{ck.pertences}</div></>}
          {ck.obs && <><h2>Observações</h2><div className="obs">{ck.obs}</div></>}

          <div className="ass">
            <div>Assinatura do cliente — confere com o estado do veículo</div>
            <div>Responsável pelo recebimento</div>
          </div>

          <div className="rodape">
            Este documento registra o estado aparente do veículo no momento da entrada.
            Avarias não listadas aqui e constatadas depois serão apuradas em conjunto.
          </div>
        </div>
  );
}

function Checklist({ db, set, toast, usuario, novaOS, acoes }) {
  const { executar, ocupado } = usarServidor(toast);
  const [modo, setModo] = useState("lista");
  const [verId, setVerId] = useState(null);
  const [f, setF] = useState(null);

  const vei = (id) => db.veiculos.find((v) => v.id === id);
  const cliDoVei = (id) => db.clientes.find((c) => c.id === vei(id)?.cli);

  /**
   * Começar um checklist.
   *
   * Lia `db.veiculos[0]` direto: numa oficina recém-instalada não existe
   * veículo, `db.veiculos[0]` é undefined e o `.id` derrubava a tela. Os dois
   * botões da aba chamam esta função, então nenhum dos dois funcionava.
   *
   * Pegar o primeiro da lista também estava errado onde existem veículos: o
   * checklist é do carro que ACABOU de chegar, não do primeiro cadastrado.
   * Agora escolhe-se qual.
   */
  const [escolhendoVeiculo, setEscolhendoVeiculo] = useState(false);
  const [buscaVei, setBuscaVei] = useState("");

  const iniciar = () => {
    if (db.veiculos.length === 0) {
      toast("Nenhum veículo cadastrado",
        "O checklist é de um carro específico. Abra uma OS pelo balcão — lá dá para cadastrar cliente e veículo na hora.", "erro");
      return;
    }
    setBuscaVei("");
    setEscolhendoVeiculo(true);
  };

  const comecarCom = (v) => {
    setEscolhendoVeiculo(false);
    setF({ vei: v.id, km: v.km, comb: 2, fotos: [], avarias: [], itens: {}, obs: "", assinado: false, pertences: "" });
    setModo("novo");
  };

  const veiculosBusca = (() => {
    const q = buscaVei.trim().toLowerCase();
    const lista = q
      ? db.veiculos.filter((v) =>
          (v.placa ?? "").toLowerCase().includes(q.replace(/[^a-z0-9]/gi, "")) ||
          [v.marca, v.modelo].filter(Boolean).join(" ").toLowerCase().includes(q) ||
          (cliDoVei(v.id)?.nome ?? "").toLowerCase().includes(q))
      : db.veiculos;
    return lista.slice(0, 12);
  })();

  const ModalVeiculo = () => (
    <Modal open={escolhendoVeiculo} onClose={() => setEscolhendoVeiculo(false)}
      title="Qual veículo está entrando?" sub="O checklist registra o estado deste carro na chegada."
      footer={<Btn variant="g" onClick={() => setEscolhendoVeiculo(false)}>Cancelar</Btn>}>
      <div className="grid gap-3">
        <div className="relative">
          <Search size={15} className="absolute t-mut2" style={{ left: 10, top: 13 }} />
          <input className="inp" style={{ paddingLeft: 32 }} autoFocus
            placeholder="Placa, modelo ou nome do cliente"
            value={buscaVei} onChange={(e) => setBuscaVei(e.target.value)} />
        </div>
        <div className="grid gap-1">
          {veiculosBusca.map((v) => (
            <button key={v.id} className="card px-3 py-2 text-left flex items-center gap-3"
              onClick={() => comecarCom(v)}>
              <Placa n={v.placa} size="sm" />
              <span className="flex-1 text-sm">{[v.marca, v.modelo].filter(Boolean).join(" ") || "sem modelo"}</span>
              <span className="text-xs t-mut">{cliDoVei(v.id)?.nome ?? ""}</span>
            </button>
          ))}
          {veiculosBusca.length === 0 && (
            <div className="text-sm t-mut text-center py-4">
              Nenhum veículo com esse termo. O cadastro de veículo novo é pelo balcão da OS.
            </div>
          )}
        </div>
      </div>
    </Modal>
  );

  const salvar = () => {
    if (!f.assinado) { toast("Falta a assinatura", "O cliente precisa assinar antes de deixar o veículo.", "alerta"); return; }
    /* Checklist sem nenhum item marcado não defende a oficina de nada: ele
       existe, parece completo e não diz o estado de peça alguma. Três assim
       foram parar no banco antes desta guarda. */
    if (!Object.values(f.itens).some(Boolean)) {
      toast("Nenhum item inspecionado",
        "Marque bom, atenção ou crítico nos itens antes de registrar a entrada.", "alerta");
      return;
    }

    // O servidor guarda itens e avarias junto com o cabeçalho, numa
    // transação: checklist com veículo e sem as avarias é pior que nenhum —
    // parece completo e não defende a oficina de nada depois.
    const porGrupo = {};
    for (const [grupo, itens] of Object.entries(GRUPOS_CHECK)) {
      porGrupo[grupo] = {};
      for (const it of itens) if (f.itens[it]) porGrupo[grupo][it] = f.itens[it];
    }

    executar(
      async () => {
        const ck = await acoes.salvarChecklist({ ...f, itensPorGrupo: porGrupo });
        // As fotos foram tiradas antes do checklist existir. Agora que ele
        // tem id, elas ganham dono — e o envio automático leva daqui.
        if (f.fotos.length) await acoes.vincularFotos(ck.id);
        return ck;
      },
      () => {
        setModo("lista");
        const criticos = Object.values(f.itens).filter((x) => x === "crit").length;
        toast("Checklist registrado",
          (criticos ? `${criticos} item(ns) crítico(s). Vale abrir o orçamento agora.` : "Veículo recebido e documentado.")
          + (f.fotos.length ? ` ${f.fotos.length} foto(s) sendo enviadas em segundo plano.` : ""));
      },
      "Não deu para registrar");
  };

  const marcar = (item, valor) => setF((s) => ({ ...s, itens: { ...s.itens, [item]: s.itens[item] === valor ? undefined : valor } }));
  const mexerAvaria = (i, novo) => setF((s) => ({ ...s, avarias: novo === null ? s.avarias.filter((_, k) => k !== i).map((a, k) => ({ ...a, n: k + 1 })) : s.avarias.map((a, k) => (k === i ? novo : a)) }));

  const SEMA = [{ v: "ok", i: Check, l: "Bom" }, { v: "warn", i: AlertTriangle, l: "Atenção" }, { v: "crit", i: X, l: "Crítico" }];

  if (modo === "novo" && f) {
    const v = vei(f.vei), c = cliDoVei(f.vei);
    const total = Object.values(GRUPOS_CHECK).flat().length;
    const feitos = Object.values(f.itens).filter(Boolean).length;
    return (
      <div className="grid gap-4">
        <div className="card p-4 flex flex-wrap items-center gap-4">
          <Placa n={v.placa} size="lg" />
          <div className="flex-1" style={{ minWidth: 180 }}>
            <div className="dsp" style={{ fontSize: 18 }}>{v.marca} {v.modelo}</div>
            <div className="text-sm t-mut">{c?.nome} · {v.ano} · {v.cor}</div>
          </div>
          <div className="text-right">
            <div className="eyebrow">Itens conferidos</div>
            <div className="dsp mono" style={{ fontSize: 22 }}>{feitos}<span className="t-mut2" style={{ fontSize: 15 }}>/{total}</span></div>
          </div>
          <Btn variant="g" icon={X} onClick={() => setModo("lista")}>Cancelar</Btn>
        </div>

        <div className="card p-4">
          <div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))" }}>
            <Field label="Veículo">
              <select className="inp" value={f.vei} onChange={(e) => { const nv = vei(e.target.value); setF({ ...f, vei: e.target.value, km: nv.km }); }}>
                {db.veiculos.map((x) => <option key={x.id} value={x.id}>{x.placa} — {x.marca} {x.modelo}</option>)}
              </select>
            </Field>
            <Field label="Hodômetro na entrada" hint="Fotografe o painel para comprovar."><input className="inp inp-mono" value={f.km} onChange={(e) => setF({ ...f, km: e.target.value })} /></Field>
            <Field label={`Combustível — ${["Reserva", "1/4", "1/2", "3/4", "Cheio"][f.comb]}`}>
              <div className="flex items-center gap-2">
                <Fuel size={16} className="t-mut2 flex-none" />
                <input type="range" min="0" max="4" value={f.comb} onChange={(e) => setF({ ...f, comb: +e.target.value })} style={{ width: "100%", accentColor: "var(--signal)" }} />
              </div>
            </Field>
            <Field label="Pertences no veículo" hint="Documentos, som, macaco, estepe."><input className="inp" value={f.pertences} onChange={(e) => setF({ ...f, pertences: e.target.value })} placeholder="Estepe, macaco, chave de roda" /></Field>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-2 mb-3"><Camera size={15} className="t-mut" /><span className="dsp" style={{ fontSize: 15 }}>Fotos do veículo</span></div>
          <Fotos fotos={f.fotos} set={(x) => setF({ ...f, fotos: x })} checklistId={null} toast={toast} />
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-2 mb-3"><Car size={15} className="t-mut" /><span className="dsp" style={{ fontSize: 15 }}>Avarias já existentes</span></div>
          <MapaAvarias pontos={f.avarias} add={(p) => setF({ ...f, avarias: [...f.avarias, p] })} del={mexerAvaria} />
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-2 mb-1"><ListChecks size={15} className="t-mut" /><span className="dsp" style={{ fontSize: 15 }}>Inspeção visual</span></div>
          <div className="text-xs t-mut2 mb-3">Bom, atenção ou crítico. Item crítico entra direto no orçamento.</div>
          <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
            {Object.entries(GRUPOS_CHECK).map(([grupo, itens]) => (
              <div key={grupo}>
                <div className="eyebrow mb-2">{grupo}</div>
                <div className="grid gap-1.5">
                  {itens.map((it) => (
                    <div key={it} className="flex items-center gap-2">
                      <span className="text-sm flex-1">{it}</span>
                      <div className="flex gap-1">
                        {SEMA.map((s) => (
                          <button key={s.v} className={`sem ${f.itens[it] === s.v ? "on-" + s.v : ""}`} title={s.l} onClick={() => marcar(it, s.v)}>
                            <s.i size={14} strokeWidth={2.6} />
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card p-4 grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
          <Field label="Queixa do cliente e observações" hint="Escreva com as palavras do cliente. Ajuda o mecânico a reproduzir o problema.">
            <textarea className="inp" rows={5} value={f.obs} onChange={(e) => setF({ ...f, obs: e.target.value })} placeholder="Ex.: barulho ao frear, volante trepidando acima de 60 km/h…" />
          </Field>
          <div>
            <label className="lbl">Assinatura do cliente</label>
            <Assinatura onChange={(t) => setF((s) => ({ ...s, assinado: t }))} />
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <Btn variant="g" onClick={() => setModo("lista")}>Descartar</Btn>
          {/* Anexar PDF pelo WhatsApp não dá do navegador. A folha de
              impressão existe: imprimir ou salvar em PDF e mandar é o
              caminho real — e é o que o botão passa a fazer. */}
          <Btn variant="o" icon={Printer} onClick={() => window.print()}>Imprimir / salvar PDF</Btn>
          <Btn variant="p" icon={Save} disabled={ocupado} onClick={salvar}>Registrar entrada</Btn>
        </div>

        {/* Sem isto o botão acima imprimia uma página em branco: a impressão
            esconde o corpo inteiro e revela só o .folha, que só existia no
            modo lista. O cliente assina o papel antes de o carro ficar, e o
            checklist ainda nem foi registrado — a folha precisa existir aqui. */}
        <FolhaChecklist ck={{ ...f, id: "(não registrado)", data: new Date().toISOString().slice(0, 10) }}
          db={db} vei={vei} cliDoVei={cliDoVei} />
      </div>
    );
  }

  const ck = verId ? db.checklists.find((x) => x.id === verId) : null;

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><div className="text-sm t-mut">Todo veículo que entra passa por aqui antes de virar orçamento.</div></div>
        <Btn variant="p" icon={ClipboardCheck} onClick={iniciar}>Novo checklist de entrada</Btn>
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))" }}>
        {db.checklists.map((k) => {
          const v = vei(k.vei), c = cliDoVei(k.vei);
          const crit = Object.values(k.itens).filter((x) => x === "crit").length;
          const warn = Object.values(k.itens).filter((x) => x === "warn").length;
          return (
            <button key={k.id} onClick={() => setVerId(k.id)} className="card text-left" style={{ cursor: "pointer", overflow: "hidden" }}>
              <div className="stripe" />
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2.5">
                  <Placa n={v?.placa || ""} size="md" />
                  <span className="mono text-xs t-mut2">{k.id}</span>
                </div>
                <div className="text-sm font-semibold">{v?.marca} {v?.modelo}</div>
                <div className="text-xs t-mut">{c?.nome}</div>
                <div className="flex flex-wrap items-center gap-1.5 mt-3">
                  {crit > 0 && <Pill t="crit" icon={X}>{crit} crítico</Pill>}
                  {warn > 0 && <Pill t="warn" icon={AlertTriangle}>{warn} atenção</Pill>}
                  {crit === 0 && warn === 0 && <Pill t="ok" icon={Check}>Tudo em ordem</Pill>}
                  {k.avarias.length > 0 && <Pill t="mut">{k.avarias.length} avaria(s)</Pill>}
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 text-xs t-mut" style={{ borderTop: "1px solid var(--line)" }}>
                  <span className="mono">{num(k.km)} km</span><span>{fdata(k.data)}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {db.checklists.length === 0 && <div className="card"><Vazio icon={ClipboardCheck} titulo="Nenhum checklist registrado" msg="Comece pelo recebimento: fotos, hodômetro, avarias e assinatura." acao={<Btn variant="p" icon={Plus} onClick={iniciar}>Novo checklist</Btn>} /></div>}
      {/* Chamada de função, não <ModalVeiculo />: componente declarado dentro
          de outro é recriado a cada render e o campo perde o foco. */}
      {ModalVeiculo()}

      {ck && <FolhaChecklist ck={ck} db={db} vei={vei} cliDoVei={cliDoVei} />}

      <Modal open={!!ck} onClose={() => setVerId(null)} w="max-w-3xl"
        title={ck ? `Checklist ${ck.id}` : ""} sub={ck ? `${vei(ck.vei)?.marca} ${vei(ck.vei)?.modelo} · ${fdata(ck.data)} · ${num(ck.km)} km` : ""}
        footer={ck && <><Btn variant="o" icon={Printer} onClick={() => window.print()}>Imprimir / PDF</Btn>
          <Btn variant="p" icon={Plus} onClick={() => { setVerId(null); novaOS(cliDoVei(ck.vei)?.id, ck.vei, ck.id); }}>Abrir orçamento a partir daqui</Btn></>}>
        {ck && (
          <div className="grid gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <Placa n={vei(ck.vei)?.placa || ""} size="lg" />
              <div><div className="text-sm font-semibold">{cliDoVei(ck.vei)?.nome}</div>
                <div className="text-xs t-mut">Recebido por {db.usuarios.find((u) => u.id === ck.resp)?.nome}</div></div>
              <div className="ml-auto flex items-center gap-1.5 text-sm"><Fuel size={15} className="t-mut2" />{["Reserva", "1/4", "1/2", "3/4", "Cheio"][ck.comb]}</div>
            </div>
            <div>
              <div className="eyebrow mb-2">Inspeção</div>
              <div className="grid gap-1" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))" }}>
                {Object.entries(ck.itens).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 text-sm py-1">
                    <span className={`sem on-${v}`} style={{ width: 20, height: 20, cursor: "default" }}>
                      {v === "ok" ? <Check size={11} strokeWidth={3} /> : v === "warn" ? <AlertTriangle size={11} strokeWidth={3} /> : <X size={11} strokeWidth={3} />}
                    </span>
                    <span className="flex-1">{k}</span>
                  </div>
                ))}
              </div>
            </div>
            {ck.avarias.length > 0 && <div><div className="eyebrow mb-2">Avarias registradas</div><MapaAvarias pontos={ck.avarias} add={() => {}} del={() => {}} leitura /></div>}
            <div><div className="eyebrow mb-2">Fotos</div><Fotos fotos={ck.fotos} set={() => {}} leitura /></div>
            <div className="bg-el brd p-3" style={{ borderRadius: "var(--r)" }}>
              <div className="eyebrow mb-1">Observações</div>
              <div className="text-sm">{ck.obs || "—"}</div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export { Checklist };
